import Image from 'next/image';
import { useRouter } from 'next/router';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'next-i18next';

import { JobPositionCard, JobPositionCardProps } from '../../legacy';
import NextLink from 'next/link';
import { Heading, Button, ButtonProps, Tabs, TabItem } from '@restworld/ui-ds-base';
import { BriefcaseIcon } from '@heroicons/react/24/outline';
import { customImageLoader, getAssetSrc, useAnalytics, cn } from '@restworld/utils-common';

import SpontaneousApplication from '../../spontaneous-application';
import { RestaurantSpontaneousApplicationBanner } from './restaurant-spontaneus-application-banner';
import { useAuth } from '@restworld/feature-authentication';
import React from 'react';
import { getRestaurantPopupInfoAtom, hasUserAppliedSpontaneousApplicationAtom } from '../../worker-entry-point';

export type RestaurantJobPositionsProps = {
  jobPositionsPerDomain: {
    domain: string;
    label: string;
    jobs: {
      id: string;
      showRestaurantDetails: boolean;
      restaurantName: string;
      city?: string;
      jobTitle: any;
      occupation: string;
      salaryMin: any;
      salaryMax: any;
      jobType: string;
      salary: any;
      restaurantLogo: any;
      publishedAt: string;
      redirectUrl: string;
      staffRequested?: number;
    }[];
  }[];
  showRestaurantDetails?: boolean;
};

export function getDomainImage({ domain, isActive }: { domain: string; isActive: boolean }) {
  switch (domain) {
    case 'bar':
      return !isActive ? 'BarIcon' : 'BarIconActive';
    case 'reception':
      return !isActive ? 'ReceptionIcon' : 'ReceptionIconActive';
    case 'sala':
      return !isActive ? 'SalaIcon' : 'SalaIconActive';
    case 'cucina':
      return !isActive ? 'CucinaIcon' : 'CucinaIconActive';
    default:
      return undefined;
  }
}

export const RestaurantJobPositions = ({
  jobPositionsPerDomain,
  showRestaurantDetails,
}: RestaurantJobPositionsProps) => {
  const { t } = useTranslation('restaurant-showcase-page');
  const noActiveJobPositions = jobPositionsPerDomain?.every(({ jobs }) => jobs.length === 0);
  return (
    <div className="flex flex-col w-full gap-6 items-start">
      <div className={cn('flex items-center gap-2 lg:flex hidden', noActiveJobPositions && 'mb-[58px]')}>
        <BriefcaseIcon className="h-6 text-purple-500" />
        <Heading size={3}>{t('open_job_positions')}</Heading>
      </div>
      {!noActiveJobPositions ? (
        <ActiveJobPositionsEl
          jobPositionsPerDomain={jobPositionsPerDomain}
          showRestaurantDetails={showRestaurantDetails}
        />
      ) : (
        <NotActiveJobPositionsEl />
      )}
    </div>
  );
};

export const ActiveJobPositionsEl = ({ jobPositionsPerDomain, showRestaurantDetails }: RestaurantJobPositionsProps) => {
  const { query } = useRouter();
  const auth = useAuth();
  const { trackEvent } = useAnalytics();
  const recordViewJobPositionEvent = React.useCallback(
    (jobProps: JobPositionCardProps) => {
      trackEvent('view_job', {
        email: auth?.user?.email,
        role: auth?.user?.type,
        origin: 'restaurant_showcase_page',
        id: jobProps?.id,
        job_title: jobProps?.jobTitle,
        occupation: jobProps?.occupation,
        salary_min: jobProps?.salaryMin,
        salary_max: jobProps?.salaryMax,
        job_type: jobProps?.jobType,
        published_at: jobProps?.publishedAt,
        restaurant_name: jobProps?.restaurantName,
        city: jobProps?.city,
      });
    },
    [auth?.user?.email, auth?.user?.type, trackEvent]
  );
  const tabs: TabItem[] =
    jobPositionsPerDomain?.map(({ jobs, label }) => ({
      label,
      content: (
        <ul className="grid xl:grid-cols-2 grid-cols-1 w-full gap-6">
          {jobs.map((v, i) => (
            <li key={i}>
              <JobPositionCard
                {...(v as unknown as JobPositionCardProps)}
                showRestaurantDetails={showRestaurantDetails}
                onLinkClick={() => {
                  recordViewJobPositionEvent(v as unknown as JobPositionCardProps);
                }}
              />
            </li>
          ))}
        </ul>
      ),
    })) || [];

  return (
    <div className="flex flex-col items-start gap-8 w-full">
      <Tabs tabs={tabs} tabPanelClassName="mt-0" />
      <RestaurantSpontaneousApplicationBanner variant="active-job-positions" slug={query['slug'] as string} />
    </div>
  );
};

export const ViewOtherRestaurantsButton = ({
  variant = 'secondary-neutral',
  fullwidth = true,
  className,
}: Partial<ButtonProps>) => {
  const { t } = useTranslation('restaurant-showcase-page');
  return (
    <NextLink href="/cerco-lavoro?explorer_type=restaurants" className="text-primary-800 cursor-pointer w-full">
      <Button variant={variant} fullwidth={fullwidth} className={className}>
        {t('explore_other_restaurants')}
      </Button>
    </NextLink>
  );
};

export const NotActiveJobPositionsEl = ({
  hideSpontaneousApplication = false,
}: {
  hideSpontaneousApplication?: boolean;
}) => {
  const [restaurantPopupInfo] = useAtom(getRestaurantPopupInfoAtom);
  const didUserSpontaneousApply = useAtomValue(hasUserAppliedSpontaneousApplicationAtom);
  const { t: showCaseT } = useTranslation('restaurant-showcase-page');
  const { t: commonT } = useTranslation('common');
  return (
    <div className="flex flex-col items-center justify-start h-full w-full gap-0">
      <Image
        src={getAssetSrc('/utils-assets/not-found.svg')}
        width={238}
        height={220}
        alt={showCaseT('restaurant_not_found')}
        loader={customImageLoader}
      />
      <div className="flex flex-col items-center lg:w-[411px] w-full mt-2.5 mb-5.5 text-center">
        <Heading size={4}>
          {didUserSpontaneousApply ? showCaseT('zero_open_positions') : showCaseT('no_open_positions_at')}
        </Heading>
        <Heading size={4}>{didUserSpontaneousApply ? showCaseT('check_out_the') : `${commonT('moment')}.`}</Heading>
        {didUserSpontaneousApply && <Heading size={4}>{showCaseT('your_application')}?</Heading>}
      </div>
      <div className="flex flex-col items-center gap-4 lg:w-[325px] w-full">
        {!hideSpontaneousApplication && (
          <SpontaneousApplication
            label={showCaseT('send_spontaneous_application')}
            restaurantID={restaurantPopupInfo?.restaurantId}
          />
        )}
        <ViewOtherRestaurantsButton />
      </div>
    </div>
  );
};
